import { z } from 'zod';
/**
 * What is blocking a slice of an inspector's day.
 *
 *   - `booking`     — a scheduled job activity (work).
 *   - `unavailable` — a staff availability record (leave, part-day
 *                     restriction, busy time) for that person.
 *   - `closed`      — a whole-of-business closure or public holiday; applies
 *                     to every inspector on that date.
 */
export const AvailabilityEventKindSchema = z.enum(['booking', 'unavailable', 'closed']);
export const AvailabilityEventSchema = z.object({
    source: z.enum(['leadingEdge', 'actnow', 'dashboard']),
    kind: AvailabilityEventKindSchema,
    start: z.string(),
    end: z.string(),
    label: z.string(),
    leadingEdgeJobId: z.string().optional(),
    /** Raw ServiceM8 `availability_type`. Absent on bookings. */
    availabilityType: z.string().optional(),
    /** Present for self-service blocks stored by this dashboard. */
    blockId: z.string().uuid().optional(),
});
export const AvailabilityBlockSchema = z.object({
    blockId: z.string().uuid(),
    start: z.string().datetime(),
    end: z.string().datetime(),
    createdAt: z.string().datetime(),
});
export const SaveAvailabilityBlockSchema = z
    .object({
    inspectorId: z.string().trim().min(1).optional(),
    start: z.string().datetime(),
    end: z.string().datetime(),
})
    .refine((value) => value.end > value.start, { message: 'end must be after start' });
/**
 * Normal business hours — the working window every capacity figure is measured
 * against, and the shape an inspector's declared availability is carved out of.
 *
 * Days are `Date.getDay()` values (0 = Sunday). A day not listed has no
 * capacity at all, which is how a Mon–Fri operation excludes weekends without
 * hardcoding "weekend".
 */
export const WeekdaySchema = z.number().int().min(0).max(6);
export const BusinessHoursSchema = z
    .object({
    /** Minutes from midnight, e.g. 480 = 08:00. */
    startMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    endMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    workingDays: z.array(WeekdaySchema).min(1).max(7),
})
    .refine((value) => value.endMinutes > value.startMinutes, {
    message: 'The end of the working day must be after the start',
    path: ['endMinutes'],
});
/**
 * Falls back to the values the forecast used before hours were configurable,
 * so an account that has never opened Settings behaves exactly as it did.
 */
export const DEFAULT_BUSINESS_HOURS = {
    startMinutes: 8 * 60,
    endMinutes: 16 * 60,
    workingDays: [1, 2, 3, 4, 5],
};
export const OrgAvailabilitySettingsSchema = z.object({
    businessHours: BusinessHoursSchema,
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
});
export const SaveOrgAvailabilitySettingsSchema = z.object({
    businessHours: BusinessHoursSchema,
});
/**
 * Health of the things this dashboard depends on, for the Settings screen.
 *
 * `disabled` is a deliberate choice rather than a fault — the ServiceM8
 * calendar sync is read but ignored, and saying so is more useful than
 * omitting it and leaving someone to wonder why diary entries never appear.
 */
export const IntegrationStatusSchema = z.enum([
    'connected',
    'degraded',
    'not-configured',
    'disabled',
]);
export const IntegrationCheckSchema = z.object({
    label: z.string(),
    status: IntegrationStatusSchema,
    detail: z.string().optional(),
});
export const IntegrationHealthSchema = z.object({
    id: z.string(),
    label: z.string(),
    /** What this integration does for us, in plain words. */
    description: z.string(),
    status: IntegrationStatusSchema,
    detail: z.string().optional(),
    checks: z.array(IntegrationCheckSchema),
});
export const IntegrationHealthReportSchema = z.object({
    generatedAt: z.string(),
    integrations: z.array(IntegrationHealthSchema),
});
export const InspectorDayStatusSchema = z.enum([
    'open',
    /** Outside configured business days — a weekend, or any day the org doesn't work. */
    'non-working',
    'closed',
    'unavailable',
]);
export const InspectorDayAvailabilitySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    status: InspectorDayStatusSchema,
    /** Gross working capacity before bookings and leave are deducted. */
    capacityMinutes: z.number().int().nonnegative(),
    /** Booked work, clipped to the working window. */
    bookedMinutes: z.number().int().nonnegative(),
    /** Leave / closures / part-day restrictions, clipped to the working window. */
    unavailableMinutes: z.number().int().nonnegative(),
    /**
     * Capacity less the *union* of bookings and unavailability — a job booked
     * inside a leave block is not deducted twice.
     */
    availableMinutes: z.number().int().nonnegative(),
    utilisationPercent: z.number().nonnegative(),
    events: z.array(AvailabilityEventSchema),
});
export const InspectorAvailabilitySchema = z.object({
    userId: z.string(),
    inspectorId: z.string(),
    displayName: z.string(),
    linkedSources: z.array(z.enum(['leadingEdge', 'actnow'])),
    days: z.array(InspectorDayAvailabilitySchema),
});
/**
 * Per-tenancy read status. Bookings and leave are separate reads against
 * separate ServiceM8 objects, so either can fail on its own — the forecast
 * degrades rather than disappearing.
 */
export const AvailabilitySourceSchema = z.object({
    id: z.enum(['leadingEdge', 'actnow']),
    label: z.string(),
    status: z.enum(['connected', 'not-configured', 'unavailable']),
    leaveStatus: z.enum(['connected', 'not-configured', 'unavailable']),
    message: z.string().optional(),
    leaveMessage: z.string().optional(),
});
export const AvailabilityForecastSchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    generatedAt: z.string(),
    assumptions: z.object({
        /** Length of a full working day under the configured business hours. */
        weekdayCapacityMinutes: z.number().int().positive(),
        /** Working window, minutes from midnight. Leave is clipped to it. */
        workdayStartMinutes: z.number().int().nonnegative(),
        workdayEndMinutes: z.number().int().positive(),
        /** `Date.getDay()` values that carry capacity. */
        workingDays: z.array(WeekdaySchema),
        /** True once at least one tenancy's availability records were read. */
        leaveIncluded: z.boolean(),
        /** Employment Hero is not wired up yet — leave here is ServiceM8's. */
        employmentHeroLeaveIncluded: z.literal(false),
    }),
    sources: z.array(AvailabilitySourceSchema),
    inspectors: z.array(InspectorAvailabilitySchema),
});
//# sourceMappingURL=availability.js.map